"use client";

import { cn } from "@/lib/utils";

import { getLimitedTimeDealSeconds } from "../limited-time-deal/get-limited-time-deal-seconds";
import { useCountdown } from "../limited-time-deal/use-countdown";
import { formatCoarseRemaining } from "./format-coarse-remaining";
import { getStoreDealLabel } from "./store-deal-utils";

const TWENTY_FOUR_HOURS_IN_SECONDS = 86_400;

type Variant = "row" | "popup";

type Props = {
  /** Deal type label from the API (e.g. "Limited time deal"). */
  type: string;
  /** ISO end time; present for Amazon, absent for Walmart. */
  endTime?: Date;
  /**
   * "row" (default): compact caption style for store rows.
   * "popup": larger style for the promotion popup's primary store.
   */
  variant?: Variant;
  className?: string;
};

/**
 * Clock icon matching the Figma "Icons/Random/Clock1/Outline" design.
 * Path data is taken verbatim from that Figma node so the glyph (open ring +
 * hands to ~7:30) matches the design pixel-for-pixel. `currentColor` lets the
 * caller drive the stroke colour via text-* utilities.
 */
function ClockIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      className={className}
    >
      <path
        d="M3.33333 5.66545H1.66667C2.86618 2.49945 6.33533 0.665392 9.69307 1.56195C13.2693 2.51685 15.3936 6.17283 14.4377 9.7278C13.4818 13.2827 9.80773 15.3905 6.23147 14.4356C3.57613 13.7266 1.72128 11.5285 1.33333 8.9884"
        stroke="currentColor"
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8 5.33227V7.99893L9.33333 9.33227"
        stroke="currentColor"
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Per-store deal indicator (COM-681).
 *
 * - endTime within 24h -> live coarse countdown ("Xh Ym left").
 * - endTime beyond 24h, or no endTime (e.g. Walmart) -> static "Limited time".
 *
 * The countdown only ticks when a valid, <=24h endTime exists, mirroring the
 * existing LimitedTimeDealBadge behaviour.
 *
 * Two visual variants match the design:
 * - "row": compact caption for store rows (clock + "Limited time" | "Xh Ym left").
 * - "popup": prominent label for the popup primary store
 *   (clock + "Limited time deal" + bold time when counting down).
 */
export function StoreDealIndicator({
  type: rawType,
  endTime,
  variant = "row",
  className,
}: Props) {
  const type = getStoreDealLabel(rawType);
  const initialSeconds = getLimitedTimeDealSeconds({
    endTime,
    discountPercent: 0,
  });
  // Keep the null check: `null <= 86_400` is true in JS, which would show a
  // countdown for deals without an endTime (e.g. Walmart).
  const showCountdown =
    initialSeconds != null && initialSeconds <= TWENTY_FOUR_HOURS_IN_SECONDS;

  const seconds = useCountdown(initialSeconds, { enabled: showCountdown });
  const remaining = formatCoarseRemaining(seconds);

  if (variant === "popup") {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-2 text-text-grayscale-subtitle",
          className,
        )}
        aria-label={showCountdown ? `${type}: ${remaining}` : type}
      >
        <ClockIcon className="size-4 shrink-0" />
        <span className="body1">{type}</span>
        {showCountdown && (
          <span
            className="body1 font-semibold"
            suppressHydrationWarning
          >
            {remaining}
          </span>
        )}
      </span>
    );
  }

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 text-text-grayscale-caption body3",
        className,
      )}
      aria-label={showCountdown ? `${type}: ${remaining}` : type}
      suppressHydrationWarning
    >
      <ClockIcon className="size-4 shrink-0" />
      {/* One span for both states so a server/client clock difference (ISR)
          only changes its text, which suppressHydrationWarning covers. */}
      <span suppressHydrationWarning>{showCountdown ? remaining : type}</span>
    </span>
  );
}
